import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  Unsubscribe,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BracketDraw, DrawGenerationEvidence, MatchResultType } from "../draws/bracketEngine";
import { DrawStatus, PublicDrawProjection } from "../draws/drawLifecycle";
import { db } from "./config";
import { withCache, invalidateCachePrefix } from "../utils/cache";

const functions = getFunctions(undefined, "europe-central2");
const manageTournamentDrawCallable = httpsCallable(functions, "manageTournamentDraw");
const TTL_DRAW_VERSIONS = 30 * 1000;

export interface DrawActor {
  uid: string;
  email: string;
  name: string;
  role: "admin" | "ranking_admin";
}

export interface TournamentDrawEntry {
  countryCode?: string;
  country?: string;
  nationality?: string;
  id: string;
  competitionId: string;
  athleteId: string;
  athleteSnapshot: {
    firstName: string;
    lastName: string;
  };
  club: string;
  category: string;
  division: "Male" | "Female";
  weightCategory: string;
}

export interface TournamentDrawRecord {
  id: string;
  drawId: string;
  competitionId: string;
  competitionName: string;
  competitionDate?: Timestamp;
  categoryKey: string;
  category: string;
  division: string;
  weightCategory: string;
  latestVersionId: string;
  activeVersionId: string;
  latestVersionNumber: number;
  publishedVersionId: string | null;
  status: DrawStatus;
  publicVisibility: boolean;
  updatedAt?: Timestamp;
  updatedBy?: DrawActor;
}

export interface TournamentDrawVersion {
  id: string;
  drawId: string;
  versionId: string;
  version: number;
  status: DrawStatus;
  competitionId: string;
  competitionName: string;
  competitionDate?: Timestamp;
  categoryKey: string;
  category: string;
  division: string;
  weightCategory: string;
  draw: BracketDraw;
  evidence: DrawGenerationEvidence;
  revisionOf: string | null;
  revisionReason: string | null;
  createdBy: DrawActor;
  createdAt?: Timestamp;
  updatedBy?: DrawActor;
  updatedAt?: Timestamp;
  lockedAt?: Timestamp;
  publishedAt?: Timestamp;
  liveAt?: Timestamp;
  finalizedAt?: Timestamp;
}

export interface PublicTournamentDraw {
  id: string;
  drawId: string;
  competitionId: string;
  competitionName: string;
  competitionDate?: Timestamp;
  categoryKey: string;
  category: string;
  division: string;
  weightCategory: string;
  versionId: string;
  version: number;
  status: "PUBLISHED" | "LIVE" | "FINAL";
  published: true;
  draw: PublicDrawProjection;
  publishedAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface PublicDrawIndexItem {
  drawId: string;
  categoryKey: string;
  category: string;
  division: string;
  weightCategory: string;
  versionId: string;
  version: number;
  status: "PUBLISHED" | "LIVE" | "FINAL";
  bracketSize: number;
}

function serializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export async function getTournamentDrawEntrants(competitionId: string): Promise<TournamentDrawEntry[]> {
  const response = await manageTournamentDrawCallable({ action: "list_entrants", competitionId });
  const data = response.data as { entries?: TournamentDrawEntry[] };
  return Array.isArray(data.entries) ? data.entries : [];
}

export async function saveTournamentDrawVersion(input: {
  drawId: string;
  competitionId: string;
  categoryKey: string;
  category: string;
  division: string;
  weightCategory: string;
  draw: BracketDraw;
  evidence: DrawGenerationEvidence;
  reason?: string;
}): Promise<{ drawId: string; versionId: string; version: number; status: DrawStatus }> {
  const response = await manageTournamentDrawCallable({
    action: "save_version",
    ...serializable(input),
  });
  invalidateCachePrefix(`tournamentDraws:${input.drawId}`);
  return response.data as { drawId: string; versionId: string; version: number; status: DrawStatus };
}

export async function transitionTournamentDraw(input: {
  drawId: string;
  versionId: string;
  targetStatus: DrawStatus | "UNPUBLISHED";
  reason?: string;
}): Promise<{ drawId: string; versionId: string; status: DrawStatus }> {
  const response = await manageTournamentDrawCallable({ action: "transition", ...input });
  invalidateCachePrefix(`tournamentDraws:${input.drawId}`);
  return response.data as { drawId: string; versionId: string; status: DrawStatus };
}

export async function saveTournamentMatchResult(input: {
  drawId: string;
  versionId: string;
  matchId: string;
  operation: "record" | "undo";
  winnerEntrantId?: string;
  resultType?: Exclude<MatchResultType, "bye">;
  reason?: string;
  expectedStateVersion: number;
}): Promise<{ drawId: string; versionId: string; stateVersion: number; draw: BracketDraw }> {
  const response = await manageTournamentDrawCallable({ action: "match_result", ...input });
  invalidateCachePrefix(`tournamentDraws:${input.drawId}`);
  return response.data as { drawId: string; versionId: string; stateVersion: number; draw: BracketDraw };
}

export async function getTournamentDrawVersions(
  drawId: string,
  fresh = false
): Promise<TournamentDrawVersion[]> {
  if (fresh) invalidateCachePrefix(`tournamentDraws:${drawId}`);
  return withCache(`tournamentDraws:${drawId}:versions`, TTL_DRAW_VERSIONS, async () => {
    const versionsQuery = query(
      collection(db, "tournamentDraws", drawId, "versions"),
      orderBy("version", "desc")
    );
    const snapshot = await getDocs(versionsQuery);
    return snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as TournamentDrawVersion));
  });
}

export function listenTournamentDrawRecord(
  drawId: string,
  callback: (record: TournamentDrawRecord | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, "tournamentDraws", drawId),
    (snapshot) => callback(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as TournamentDrawRecord) : null),
    onError
  );
}

export function listenPublicDrawIndex(
  competitionId: string,
  callback: (draws: PublicDrawIndexItem[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, "publicDrawIndexes", competitionId),
    (snapshot) => callback(snapshot.exists() && Array.isArray(snapshot.data().draws) ? snapshot.data().draws : []),
    onError
  );
}

export function listenPublicDraw(
  drawId: string,
  callback: (draw: PublicTournamentDraw | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, "publicDraws", drawId),
    (snapshot) => callback(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as PublicTournamentDraw) : null),
    onError
  );
}
